import { Trans, useTranslation } from 'next-i18next';
import Link from 'next-translate-routes/link';
import { useRouter } from 'next-translate-routes/router';
import React from 'react';

import { Button, Icon } from '@/components';
import { DepositOpeningGuide } from '@/components/DepositOpeningGuide';
import { STATIC_GUIDE_STEPS } from '@/components/DepositOpeningGuide/utils';
import { useCmsContent } from '@/contexts/CmsContentContext';
import { COUNTRIES_PATHS } from '@/utils/countries';
import { termKeys } from '@/utils/termKeys';

export const useSeoText = () => {
  const { t } = useTranslation();
  const { country: selectedCountry } = useCmsContent();
  const router = useRouter();

  const getTranslatedCountries = (countries: any[]) => {
    return countries
      .map((country) => t(`database.countries.${country.name}`))
      .join(', ');
  };
  const getFixedDepositsSections = ({
    country,
    maxRate,
    monthYear,
  }: {
    country: string;
    maxRate: number;
    monthYear: string;
  }) => {
    const translatedCountry = t(`database.countries.${country}`);
    return [
      {
        title: t('sections.fixed-deposits.first.title'),
        content: (
          <>
            <p className="body2 mb-3 tracking-[0.2px] text-grey-800 last:mb-0">
              {t('sections.fixed-deposits.first.text1')}
            </p>
            <p className="body2 mb-3 tracking-[0.2px] text-grey-800 last:mb-0">
              {t('sections.fixed-deposits.first.text2')}
            </p>
            <p className="body2 mb-3 tracking-[0.2px] text-grey-800 last:mb-0">
              {t('sections.fixed-deposits.first.text3')}
            </p>
            <p className="body2 mb-3 tracking-[0.2px] text-grey-800 last:mb-0">
              {t('sections.fixed-deposits.first.text4')}
            </p>
          </>
        ),
      },
      {
        title: t('sections.fixed-deposits.second.title'),
        content: (
          <>
            <p className="body2 mb-3 tracking-[0.2px] text-grey-800">
              {t('sections.fixed-deposits.second.text1')}
            </p>
            <p className="body2 mb-3 tracking-[0.2px] text-grey-800">
              {t('sections.fixed-deposits.second.text2')}
            </p>
            <p className="body2 mb-3 tracking-[0.2px] text-grey-800">
              {t('sections.fixed-deposits.second.text3')}
            </p>
            <p className="body2 mb-6 tracking-[0.2px] text-grey-800">
              {t('sections.fixed-deposits.second.text4')}
            </p>
            <p className="body2 mb-3 tracking-[0.2px] text-grey-800">
              {t('sections.fixed-deposits.second.text5')}
            </p>
            <ul>
              <li>{t('sections.fixed-deposits.second.text6')}</li>
              <li>{t('sections.fixed-deposits.second.text7')}</li>
              <li>{t('sections.fixed-deposits.second.text8')}</li>
              <li>{t('sections.fixed-deposits.second.text9')}</li>
            </ul>
          </>
        ),
      },
      {
        title: t('sections.fixed-deposits.third.title'),
        content: (
          <>
            <p className="body2 mb-3 tracking-[0.2px] text-grey-800 last:mb-0">
              {t('sections.fixed-deposits.third.text1')}
            </p>
            <p className="body2 mb-3 tracking-[0.2px] text-grey-800 last:mb-0">
              {t('sections.fixed-deposits.third.text2')}
            </p>
            <p className="body2 mb-3 tracking-[0.2px] text-grey-800 last:mb-0">
              {t('sections.fixed-deposits.third.text3')}
            </p>
            <p className="body2 mb-3 tracking-[0.2px] text-grey-800 last:mb-0">
              {t('sections.fixed-deposits.third.text4', {
                country: translatedCountry,
                maxRate,
                monthYear,
              })}
            </p>
          </>
        ),
      },
      {
        title: t('sections.fixed-deposits.fourth.title', {
          country: translatedCountry,
        }),
        content: (
          <>
            <p className="body2 mb-8 tracking-[0.2px] text-grey-800">
              {t('sections.fixed-deposits.fourth.text1')}
            </p>
            <p className="body2 mb-8 tracking-[0.2px] text-grey-800">
              {t('sections.fixed-deposits.fourth.text2')}
            </p>
            <h3 className="body1-bold mb-3 tracking-[0.2px] text-blue-900">
              {t('sections.fixed-deposits.fourth.text3')}
            </h3>
            <p className="body2 mb-6 tracking-[0.2px] text-grey-800">
              {t('sections.fixed-deposits.fourth.text4')}
            </p>
            <h3 className="body2-bold mb-1.5 tracking-[0.2px] text-blue-900">
              {t('sections.fixed-deposits.fourth.text5')}
            </h3>
            <p className="body2 mb-6 tracking-[0.2px] text-grey-800">
              {t('sections.fixed-deposits.fourth.text6')}
            </p>
            <h3 className="body2-bold mb-1.5 tracking-[0.2px] text-blue-900">
              {t('sections.fixed-deposits.fourth.text7')}
            </h3>
            <p className="body2 mb-3 tracking-[0.2px] text-grey-800">
              {t('sections.fixed-deposits.fourth.text8')}
            </p>
            <h3 className="body2-bold mb-1.5 tracking-[0.2px] text-blue-900">
              {t('sections.fixed-deposits.fourth.text9')}
            </h3>
            <p className="body2 mb-3 tracking-[0.2px] text-grey-800">
              {t('sections.fixed-deposits.fourth.text10')}
            </p>
            <ul className="mb-6">
              <li>
                <Link
                  href={`/fixed-deposits/${
                    COUNTRIES_PATHS[selectedCountry.name][
                      router.locale as string
                    ]
                  }/${termKeys['6'][router.locale as string]}/eur`}
                  shallow={false}
                >
                  {t('sections.fixed-deposits.fourth.text11')}
                </Link>
              </li>
              <li>
                <Link
                  href={`/fixed-deposits/${
                    COUNTRIES_PATHS[selectedCountry.name][
                      router.locale as string
                    ]
                  }/${termKeys['12'][router.locale as string]}/eur`}
                  shallow={false}
                >
                  {t('german_deposits.blocks.fifth.list_two')}
                </Link>
              </li>
              <li>
                <Link
                  href={`/fixed-deposits/${
                    COUNTRIES_PATHS[selectedCountry.name][
                      router.locale as string
                    ]
                  }/${termKeys['24'][router.locale as string]}/eur`}
                  shallow={false}
                >
                  {t('german_deposits.blocks.fifth.list_three')}
                </Link>
              </li>
              <li>
                <Link
                  href={`/fixed-deposits/${
                    COUNTRIES_PATHS[selectedCountry.name][
                      router.locale as string
                    ]
                  }/${termKeys['36'][router.locale as string]}/eur`}
                  shallow={false}
                >
                  {t('german_deposits.blocks.fifth.list_four')}
                </Link>
              </li>
            </ul>
            <h3 className="h5 mb-3 text-blue-900">
              {t('german_deposits.blocks.fifth.subtitle_six')}
            </h3>
            <p className="body2 mb-8 tracking-[0.2px] text-grey-800">
              {t('german_deposits.blocks.fifth.text_seven')}
            </p>
            <DepositOpeningGuide
              steps={STATIC_GUIDE_STEPS}
              className="mb-7"
              containerClassName="md:gap-[46px] md:mr-6"
            />
            <p className="body2 tracking-[0.2px] text-grey-800">
              {t('german_deposits.blocks.fifth.text_eight')}
            </p>
          </>
        ),
      },
      {
        title: t('sections.fixed-deposits.five.title'),
        content: (
          <>
            <h3 className="body2-bold mb-1.5 tracking-[0.2px] text-blue-900">
              {t('sections.fixed-deposits.five.text1')}
            </h3>
            <p className="body2 mb-6 tracking-[0.2px] text-grey-800">
              {t('sections.fixed-deposits.five.text2')}
            </p>
            <h3 className="body2-bold mb-1.5 tracking-[0.2px] text-blue-900">
              {t('german_deposits.blocks.sixth.subtitle_two')}
            </h3>
            <p className="body2 mb-6 tracking-[0.2px] text-grey-800">
              {t('german_deposits.blocks.sixth.text_two')}
            </p>
            <h3 className="body2-bold mb-1.5 tracking-[0.2px] text-blue-900">
              {t('german_deposits.blocks.sixth.subtitle_three')}
            </h3>
            <p className="body2 mb-6 tracking-[0.2px] text-grey-800">
              {t('german_deposits.blocks.sixth.text_three')}
            </p>
            <h3 className="body2-bold mb-1.5 tracking-[0.2px] text-blue-900">
              {t('german_deposits.blocks.sixth.subtitle_four')}
            </h3>
            <p className="body2 mb-6 tracking-[0.2px] text-grey-800">
              {t('german_deposits.blocks.sixth.text_four')}
            </p>
            <h3 className="body2-bold mb-1.5 tracking-[0.2px] text-blue-900">
              {t('german_deposits.blocks.sixth.subtitle_five')}
            </h3>
            <p className="body2 mb-6 tracking-[0.2px] text-grey-800">
              {t('german_deposits.blocks.sixth.text_five')}
            </p>
            <h3 className="body2-bold mb-1.5 tracking-[0.2px] text-blue-900">
              {t('german_deposits.blocks.sixth.subtitle_six')}
            </h3>
            <p className="body2 mb-3 tracking-[0.2px] text-grey-800">
              {t('german_deposits.blocks.sixth.text_six')}
            </p>
            <p className="body2 tracking-[0.2px] text-grey-800">
              {t('german_deposits.blocks.sixth.text_seven')}
            </p>
          </>
        ),
      },
      {
        title: t('german_deposits.blocks.seventh.title'),
        content: (
          <>
            <p className="body2 mb-6 tracking-[0.2px] text-grey-800">
              {t('german_deposits.blocks.seventh.text')}
            </p>
            <Link
              href="/fixed-deposits"
              className="mx-auto block w-max"
              shallow={false}
            >
              <Button color="blue" className="w-max gap-1 px-[24px] py-[18px]">
                <>
                  <p className="body2-bold tracking-[0.2px] text-white">
                    {t('german_deposits.blocks.seventh.action')}
                  </p>
                  <Icon
                    name="arrowRightWhite"
                    className="size-4 text-transparent"
                  />
                </>
              </Button>
            </Link>
          </>
        ),
      },
    ];
  };

  const getFixedDepositsStatisticsSections = () => {
    return [
      {
        title: t('sections.fixed-statistics.one.title'),
        content: (
          <>
            <p className="body2 mb-3 tracking-[0.2px] text-grey-800 last:mb-0">
              {t('sections.fixed-statistics.one.text1')}
            </p>
            <p className="body2 mb-3 tracking-[0.2px] text-grey-800 last:mb-0">
              {t('sections.fixed-statistics.one.text2')}
            </p>
            <p className="body2 mb-3 tracking-[0.2px] text-grey-800 last:mb-0">
              {t('sections.fixed-statistics.one.text3')}
            </p>
            <p className="body2 mb-3 tracking-[0.2px] text-grey-800 last:mb-0">
              {t('sections.fixed-statistics.one.text4')}
            </p>
          </>
        ),
      },
      {
        title: t('sections.fixed-statistics.two.title'),
        content: (
          <>
            <p className="body2 mb-3 tracking-[0.2px] text-grey-800">
              <Trans
                i18nKey="sections.fixed-statistics.two.text1"
                t={t}
                components={{
                  blueLink: (
                    <a
                      aria-label="ECB euro area bank interest rates"
                      href="https://data.ecb.europa.eu/main-figures/bank-interest-rates/deposits"
                      target="_blank"
                      rel="nofollow"
                      className="text-blue-500"
                    />
                  ),
                }}
              />
            </p>
            <p className="body2 mb-3 tracking-[0.2px] text-grey-800">
              {t('sections.fixed-statistics.two.text2')}
            </p>
            <p className="body2 mb-3 tracking-[0.2px] text-grey-800">
              {t('sections.fixed-statistics.two.text3')}
            </p>
            <p className="body2 mb-3 tracking-[0.2px] text-grey-800">
              {t('sections.fixed-statistics.two.text4')}
            </p>
            <p className="body2 mb-3 tracking-[0.2px] text-grey-800">
              {t('sections.fixed-statistics.two.text5')}
            </p>
          </>
        ),
      },
      {
        title: t('sections.fixed-statistics.three.title'),
        content: (
          <p className="body2 mb-3 tracking-[0.2px] text-grey-800 last:mb-0">
            {t('sections.fixed-statistics.three.text1')}
          </p>
        ),
      },
      {
        title: t('sections.fixed-statistics.four.title'),
        content: (
          <>
            <p className="body2 mb-6 tracking-[0.2px] text-grey-800">
              {t('sections.fixed-statistics.four.text0')}
            </p>
            <h3 className="body2-bold mb-1.5 tracking-[0.2px] text-blue-900">
              {t('sections.fixed-statistics.four.text1')}
            </h3>
            <p className="body2 mb-1.5 tracking-[0.2px] text-grey-800">
              {t('sections.fixed-statistics.four.text2')}
            </p>
            <p className="body2 mb-1.5 tracking-[0.2px] text-grey-800">
              {t('sections.fixed-statistics.four.text3')}
            </p>
            <p className="body2 mb-1.5 tracking-[0.2px] text-grey-800">
              {t('sections.fixed-statistics.four.text4')}
            </p>
            <p className="body2 mb-1.5 tracking-[0.2px] text-grey-800">
              {t('sections.fixed-statistics.four.text5')}
            </p>
            <Link
              target="_blank"
              rel="nofollow"
              href="https://www.ecb.europa.eu/stats/policy_and_exchange_rates/key_ecb_interest_rates/html/index.en.html"
              className="body2 mb-6 block tracking-[0.2px] text-grey-800"
            >
              https://www.ecb.europa.eu/stats/policy_and_exchange_rates/key_ecb_interest_rates/html/index.en.html
            </Link>
            <h3 className="body2-bold mb-1.5 tracking-[0.2px] text-blue-900">
              {t('sections.fixed-statistics.four.text6')}
            </h3>
            <p className="body2 mb-6 tracking-[0.2px] text-grey-800">
              {t('sections.fixed-statistics.four.text7')}
            </p>
            <h3 className="body2-bold mb-1.5 tracking-[0.2px] text-blue-900">
              {t('sections.fixed-statistics.four.text8')}
            </h3>
            <p className="body2 mb-6 tracking-[0.2px] text-grey-800">
              {t('sections.fixed-statistics.four.text9')}
            </p>
            <h3 className="body2-bold mb-1.5 tracking-[0.2px] text-blue-900">
              {t('sections.fixed-statistics.four.text10')}
            </h3>
            <p className="body2 mb-6 tracking-[0.2px] text-grey-800">
              {t('sections.fixed-statistics.four.text11')}
            </p>
            <h3 className="body2-bold mb-1.5 tracking-[0.2px] text-blue-900">
              {t('sections.fixed-statistics.four.text12')}
            </h3>
            <p className="body2 mb-6 tracking-[0.2px] text-grey-800">
              {t('sections.fixed-statistics.four.text13')}
            </p>
          </>
        ),
      },
    ];
  };

  const getSavingsAccountsSections = ({
    country,
    monthYear,
  }: {
    country: string;
    maxRate: number;
    monthYear: string;
  }) => {
    const translatedCountry = t(`database.countries.${country}`);
    return [
      {
        title: t('sections.savings-accounts.one.title'),
        content: (
          <>
            <p className="body2 mb-3 tracking-[0.2px] text-grey-800 last:mb-0">
              {t('sections.savings-accounts.one.text1')}
            </p>
            <p className="body2 mb-3 tracking-[0.2px] text-grey-800 last:mb-0">
              {t('sections.savings-accounts.one.text2')}
            </p>
          </>
        ),
      },
      {
        title: t('sections.savings-accounts.two.title'),
        content: (
          <p className="body2 mb-3 tracking-[0.2px] text-grey-800">
            {t('sections.savings-accounts.two.text1')}
          </p>
        ),
      },
      {
        title: t('sections.savings-accounts.three.title'),
        content: (
          <>
            <p className="body2 mb-3 tracking-[0.2px] text-grey-800 last:mb-0">
              {t('sections.savings-accounts.three.text1')}
            </p>
            <p className="body2 mb-3 tracking-[0.2px] text-grey-800 last:mb-0">
              {t('sections.savings-accounts.three.text2')}
            </p>
          </>
        ),
      },
      {
        title: t('sections.savings-accounts.four.title', {
          monthYear,
          country: translatedCountry,
        }),
        content: (
          <ul className="mb-6">
            <li>
              <Link
                href={`/savings-accounts/${
                  COUNTRIES_PATHS[selectedCountry.name][router.locale as string]
                }/`}
                shallow={false}
              >
                {t('sections.savings-accounts.four.text1')}
              </Link>
            </li>
            <li>
              <Link
                href={`/fixed-deposits/${
                  COUNTRIES_PATHS[selectedCountry.name][router.locale as string]
                }/${termKeys['6'][router.locale as string]}/eur`}
                shallow={false}
              >
                {t('sections.savings-accounts.four.text2')}
              </Link>
            </li>
            <li>
              <Link
                href={`/fixed-deposits/${
                  COUNTRIES_PATHS[selectedCountry.name][router.locale as string]
                }/${termKeys['12'][router.locale as string]}/eur`}
                shallow={false}
              >
                {t('german_deposits.blocks.fifth.list_two')}
              </Link>
            </li>
            <li>
              <Link
                href={`/fixed-deposits/${
                  COUNTRIES_PATHS[selectedCountry.name][router.locale as string]
                }/${termKeys['24'][router.locale as string]}/eur`}
                shallow={false}
              >
                {t('german_deposits.blocks.fifth.list_three')}
              </Link>
            </li>
            <li>
              <Link
                href={`/fixed-deposits/${
                  COUNTRIES_PATHS[selectedCountry.name][router.locale as string]
                }/${termKeys['36'][router.locale as string]}/eur`}
                shallow={false}
              >
                {t('german_deposits.blocks.fifth.list_four')}
              </Link>
            </li>
          </ul>
        ),
      },
      {
        title: t('sections.savings-accounts.five.title'),
        content: (
          <>
            <h3 className="body2-bold mb-1.5 tracking-[0.2px] text-blue-900">
              {t('sections.savings-accounts.five.text1')}
            </h3>
            <p className="body2 mb-6 tracking-[0.2px] text-grey-800">
              {t('sections.savings-accounts.five.text2', {
                country: translatedCountry,
              })}
            </p>
            <h3 className="body2-bold mb-1.5 tracking-[0.2px] text-blue-900">
              {t('sections.savings-accounts.five.text3')}
            </h3>
            <p className="body2 mb-6 tracking-[0.2px] text-grey-800">
              {t('sections.savings-accounts.five.text4')}
            </p>
            <h3 className="body2-bold mb-1.5 tracking-[0.2px] text-blue-900">
              {t('sections.savings-accounts.five.text5')}
            </h3>
            <p className="body2 mb-6 tracking-[0.2px] text-grey-800">
              {t('sections.savings-accounts.five.text6')}
            </p>
            <p className="body2 mb-6 tracking-[0.2px] text-grey-800">
              {t('sections.savings-accounts.five.text7')}
            </p>
            <p className="body2 mb-6 tracking-[0.2px] text-grey-800">
              {t('sections.savings-accounts.five.text8')}
            </p>
            <p className="body2 mb-6 tracking-[0.2px] text-grey-800">
              {t('sections.savings-accounts.five.text9')}
            </p>
            <h3 className="body2-bold mb-1.5 tracking-[0.2px] text-blue-900">
              {t('sections.savings-accounts.five.text10')}
            </h3>
            <p className="body2 mb-6 tracking-[0.2px] text-grey-800">
              {t('sections.savings-accounts.five.text11')}
            </p>
            <h3 className="body2-bold mb-1.5 tracking-[0.2px] text-blue-900">
              {t('sections.savings-accounts.five.text12')}
            </h3>
            <p className="body2 mb-6 tracking-[0.2px] text-grey-800">
              {t('sections.savings-accounts.five.text13')}
            </p>
          </>
        ),
      },
    ];
  };

  const getBankSections = ({
    country,
    bankName,
    countriesOfAvailability,
    numberOfFixedDeposits = 0,
    numberOfSavingsAccounts = 0,
    currenciesList,
    offers,
  }: {
    country: string;
    bankName: string;
    countriesOfAvailability: any[];
    numberOfFixedDeposits?: number;
    numberOfSavingsAccounts?: number;
    currenciesList: any[];
    offers: any[];
  }) => {
    const translatedCountry = t(`database.countries.${country}`);
    const currencies = currenciesList
      .map((item) => {
        return item.currency;
      })
      .join(', ');
    return [
      {
        title: t('sections.bank.two.title', {
          bankName,
        }),
        content: (
          <p className="text-[15px] tracking-[0.2px] text-grey-800">
            {t('sections.bank.two.text1', {
              bankName,
              country: translatedCountry,
            })}
          </p>
        ),
      },
      {
        title: t('sections.bank.three.title', {
          bankName,
        }),
        content: (
          <p className="text-[15px] tracking-[0.2px] text-grey-800">
            {t('sections.bank.three.text1', {
              bankName,
              countriesOfAvailability: getTranslatedCountries(
                countriesOfAvailability,
              ),
            })}
          </p>
        ),
      },
      {
        title: t('sections.bank.four.title', {
          bankName,
        }),
        content: (
          <>
            <p className="text-[15px] tracking-[0.2px] text-grey-800">
              {t('sections.bank.four.text1', {
                bankName,
                countriesOfAvailability: currencies,
                numberOfFixedDeposits,
                numberOfSavingsAccounts,
              })}
            </p>
            {offers.map((offer: any, index) => (
              <p
                key={index}
                className="text-[15px] tracking-[0.2px] text-grey-800"
              >
                {t('sections.bank.four.text2', {
                  bankName,
                  countriesOfAvailability: offer.uniqueCountries,
                  currency: offer.currency,
                  ratesList: offer.termDetails,
                })}
              </p>
            ))}
          </>
        ),
      },
    ];
  };

  return {
    getFixedDepositsSections,
    getSavingsAccountsSections,
    getBankSections,
    getFixedDepositsStatisticsSections,
  };
};
